import { useCallback } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  getActiveGenerationRunId,
  resolveGenerationRunId,
  setActiveGenerationRun,
} from '../utils/generationRunContext';
import type { ParsedReservationError } from '../utils/generationRunContext';
import {
  logManuscriptCreditRequiredDiagnostic,
  logManuscriptReservationContractFailure,
  resolveReservationRejectionOutcome,
  type PaywallModalState,
} from '../utils/manuscriptCreditRouting.service';
import {
  ACCEPTED_RESERVATION_OPERATIONS,
  normalizeReservationOperation,
} from '../utils/reservationOperation.service';
import { AppStatus } from '../types';
import {
  type GenerationCallbackResult,
  type ReservationRecoveryState,
  isReservationRecoveryExpired,
  normalizeGenerationCallbackResult,
  shouldSkipReservationForRecovery,
} from '../utils/reservationRecovery.service';

export type {
  GenerationCallbackResult,
  ReservationRecoveryState,
} from '../utils/reservationRecovery.service';

export type PausedRecoveryMetadata = {
  failedChapterNumber: number;
  failedAgentName: string;
  lastCompletedAgentName: string;
  retryAvailable: boolean;
  safeResumePoint: 'PublicationEditor' | 'Archivist' | 'SaveChapter' | 'PHYSICAL_CONTINUITY_EXTRACTION' | 'INSPECTOR_VALIDATION';
  archivistRawSnapshot?: Record<string, unknown>;
  inspectorPhysicalStateChanges?: import('../types').InspectorPhysicalStateChange[];
  reservationRecoveryState?: ReservationRecoveryState;
  reservationRecoveryRunId?: string;
  draftAvailable: boolean;
  editedDraftAvailable: boolean;
  checkpointStoragePath?: string;
  pausedAt?: string;
  errorType?: string;
  userFacingMessage?: string;
  recoveryStatus?: 'paused' | 'resuming' | 'resolved' | 'failed';
  activeRunId?: string;
  checkpointCreatedAt?: string;
  checkpointUpdatedAt?: string;
  expiresAt?: string;
  projectId?: string;
  failureCategory?: import('../utils/recoveryAction.service').InspectorFailureCategory;
};

export function hasResumableGenerationOperation(
  metadata: PausedRecoveryMetadata | null | undefined,
): boolean {
  return !!(
    metadata &&
    metadata.recoveryStatus !== 'resolved' &&
    (metadata.retryAvailable ||
      metadata.draftAvailable ||
      !!metadata.checkpointStoragePath)
  );
}

export type ReserveTokensResult = { ok: true } | { ok: false; error?: ParsedReservationError };

export interface GenerationLifecycleProps {
  projectId: string;
  uid: string;
  chapterNum: number;
  operation: 'rewrite' | 'free_preview' | 'manuscript_generation';
  runId?: string;
  reuseReservation?: {
    runId: string;
    state: ReservationRecoveryState;
    pausedAt?: string;
    expiresAt?: string;
  };
  reserveTokens: (
    runId: string,
    projectId: string,
    chapterNum: number,
    operation: string,
    amount: number,
    entitlement?: any,
  ) => Promise<ReserveTokensResult>;
  consumeTokenReservation: (runId: string) => Promise<boolean>;
  releaseTokenReservation: (runId: string, failureReason?: string) => Promise<boolean>;

  isProjectUnlocked: () => boolean;
  verifiedChapterCount: number;

  addLog: (agent: string, message: string, type: string, target?: string) => void;
  setStatus: (status: any) => void;
  setIsAutoRunning: (val: boolean) => void;
  setPaywallModal: (state: PaywallModalState) => void;
  onReservationBlocked?: (error: ParsedReservationError) => void;
  clearGenerationUi?: () => void;
  refreshUserData?: () => Promise<void>;
  targetChapterCount?: number;
  manuscriptCreditBalances?: Partial<Record<'short_manuscript' | 'standard_manuscript' | 'large_manuscript' | 'epic_manuscript', number>>;

  generationCallback: (runId: string) => Promise<GenerationCallbackResult>;

  initialProjectData?: {
    root?: Record<string, unknown>;
    state?: Record<string, unknown>;
  };
}

export async function finishGenerationRun(
  uid: string,
  projectId: string,
  runId: string,
  status: 'idle' | 'failed' | 'needs_user_review' | 'metadata_repair_required',
  reason?: string
): Promise<void> {
  const projectRef = doc(db, 'users', uid, 'projects', projectId);
  try {
    const snap = await getDoc(projectRef);
    if (!snap.exists()) return;
    const data = snap.data();
    if (data.activeRunId !== runId) {
      console.warn('[finishGenerationRun] Skipping cleanup — activeRunId mismatch.', {
        expected: runId,
        actual: data.activeRunId
      });
      return;
    }
    const update: Record<string, any> = {
      generationStatus: status,
      activeRunId: null,
      activeChapterNum: null,
      lastGenerationHeartbeatAt: null,
      generationFinishedAt: new Date().toISOString()
    };
    if (reason) {
      update.lastGenerationFailureReason = reason;
    }
    await setDoc(projectRef, update, { merge: true });
  } catch (e: any) {
    console.warn('[finishGenerationRun] Cleanup write failed:', e.message);
  }
}

async function ensureProjectDocumentExists(
  uid: string,
  projectId: string,
  initialProjectData: GenerationLifecycleProps['initialProjectData'],
  addLog: GenerationLifecycleProps['addLog'],
) {
  const projectRef = doc(db, 'users', uid, 'projects', projectId);
  const stateRef = doc(db, 'users', uid, 'projects', projectId, 'state', 'current');
  try {
    const snap = await getDoc(projectRef);
    if (!snap.exists()) {
      addLog('System', 'Project document missing in Firestore. Creating it now...', 'warning');
      const baselineData = initialProjectData?.root || {
        id: projectId,
        ownerId: uid,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
      };
      await setDoc(projectRef, baselineData, { merge: true });
      if (initialProjectData?.state) {
        await setDoc(stateRef, initialProjectData.state, { merge: true });
      }
      return true;
    }
    if (initialProjectData?.state) {
      const stateSnap = await getDoc(stateRef);
      if (!stateSnap.exists()) {
        await setDoc(stateRef, initialProjectData.state, { merge: true });
      }
    }
    return true;
  } catch (e: any) {
    addLog('System', `Failed to verify or create project document: ${e.message}`, 'error');
    return false;
  }
}

async function refreshServerGenerationLease(runId: string): Promise<void> {
  try {
    const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || window.location.origin;
    const { auth } = await import('../firebaseConfig');
    const user = auth.currentUser;
    if (!user) return;
    const idToken = await user.getIdToken();
    await fetch(`${API_BASE}/api/generation/heartbeat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ runId }),
    });
  } catch {
    // Heartbeat failures are non-fatal; reconciliation handles expiry.
  }
}

async function writeGenerationHeartbeat(uid: string, projectId: string, runId: string, chapterNum: number) {
  const projectRef = doc(db, 'users', uid, 'projects', projectId);
  await setDoc(
    projectRef,
    {
      activeRunId: runId,
      activeChapterNum: chapterNum,
      generationStatus: 'running',
      lastGenerationHeartbeatAt: new Date().toISOString(),
    },
    { merge: true },
  );
  await refreshServerGenerationLease(runId);
}

export function useGenerationLifecycle() {
  const runGenerationLifecycle = useCallback(async (props: GenerationLifecycleProps) => {
    const {
      projectId, uid, chapterNum, operation, runId: providedRunId,
      reuseReservation,
      reserveTokens, consumeTokenReservation, releaseTokenReservation,
      isProjectUnlocked, verifiedChapterCount,
      addLog, setStatus, setIsAutoRunning, setPaywallModal, onReservationBlocked,
      clearGenerationUi, refreshUserData, targetChapterCount, manuscriptCreditBalances,
      generationCallback, initialProjectData
    } = props;

    if (!projectId || !uid) {
      addLog('System', 'Generation blocked: Project context missing.', 'error');
      setIsAutoRunning(false);
      return false;
    }

    const docExists = await ensureProjectDocumentExists(uid, projectId, initialProjectData, addLog);
    if (!docExists) {
      addLog('System', 'Project setup is still saving. Please retry in a moment.', 'error');
      setIsAutoRunning(false);
      return false;
    }

    if (!isProjectUnlocked() && verifiedChapterCount >= 1) {
      addLog('System', 'Free Preview includes one chapter. Apply a Manuscript Credit to continue this project.', 'error');
      setPaywallModal({ isOpen: true, reason: 'generation' });
      setIsAutoRunning(false);
      return false;
    }

    const runId = resolveGenerationRunId(
      reuseReservation?.runId || providedRunId || getActiveGenerationRunId(),
    );
    setActiveGenerationRun(runId, projectId);
    const projectRef = doc(db, 'users', uid, 'projects', projectId);

    if (
      reuseReservation &&
      reuseReservation.state === 'AWAITING_PHYSICAL_EXTRACTION' &&
      isReservationRecoveryExpired(reuseReservation.pausedAt, Date.now(), reuseReservation.expiresAt)
    ) {
      addLog('System', 'The recoverable chapter charge expired. Your draft checkpoint remains available for Retry Validation while within the retention window.', 'warning');
      await releaseTokenReservation(runId, 'reservation_recovery_expired');
      setIsAutoRunning(false);
      setActiveGenerationRun(null, null);
      return false;
    }

    try {
      const snap = await getDoc(projectRef);
      if (snap.exists()) {
        const data = snap.data();
        const lockActive = data.generationStatus === 'running' || data.generationStatus === 'saving';
        if (lockActive && data.activeRunId && data.activeRunId !== runId) {
          const lockTs = data.lastGenerationHeartbeatAt || data.generationStartedAt;
          const lockAge = lockTs ? Date.now() - new Date(lockTs).getTime() : Infinity;
          const STALE_TIMEOUT_MS = 30 * 60 * 1000;

          if (lockAge < STALE_TIMEOUT_MS) {
            addLog('System', 'Another generation is already active for this project.', 'error');
            onReservationBlocked?.({
              kind: 'rate_limited',
              message: 'Another generation is already active for this project.',
              activeRunId: data.activeRunId,
              retryAfterSeconds: 60,
            });
            setIsAutoRunning(false);
            setActiveGenerationRun(null, null);
            return false;
          }

          addLog('System', 'Previous generation did not finish. The project has been safely unlocked. You can retry now.', 'warning');
          await setDoc(projectRef, {
            generationStatus: 'failed',
            activeRunId: null,
            activeChapterNum: null,
            lastGenerationHeartbeatAt: null,
            lastGenerationFailureReason: 'stale_timeout'
          }, { merge: true });
        }
      }
    } catch (e: any) {
      addLog('System', `Failed to inspect generation lock: ${e.message}`, 'error');
      setIsAutoRunning(false);
      setActiveGenerationRun(null, null);
      return false;
    }

    const normalizedOperation = normalizeReservationOperation(operation);
    if (!normalizedOperation) {
      logManuscriptReservationContractFailure({
        operation,
        normalizedOperation: null,
        acceptedOperations: [...ACCEPTED_RESERVATION_OPERATIONS],
        projectId,
        chapterNumber: chapterNum,
        paywallOpened: false,
      });
      addLog(
        'System',
        'Chronovela could not start this generation because the reservation request was invalid. No credit was used. Please try again.',
        'error',
      );
      setIsAutoRunning(false);
      setStatus(AppStatus.IDLE);
      clearGenerationUi?.();
      setPaywallModal({ isOpen: false, reason: 'generation' });
      setActiveGenerationRun(null, null);
      return false;
    }

    const amount = normalizedOperation === 'rewrite' ? 1 : 0;
    const projectSnap = await getDoc(projectRef);
    const projectData = projectSnap.exists() ? projectSnap.data() : null;

    const currentTier = projectData?.creditTier || initialProjectData?.root?.creditTier || 'unknown';
    const maxChapters = projectData?.maxChaptersAllowed || initialProjectData?.root?.maxChaptersAllowed || 1;
    const exportUnlocked = projectData?.exportUnlocked || false;
    const paidEntitlementId = projectData?.paidEntitlementId || null;

    let reservationMade = false;
    const requiresReservation =
      normalizedOperation === 'manuscript_generation' ||
      normalizedOperation === 'free_preview' ||
      normalizedOperation === 'rewrite';
    const skipReservation = shouldSkipReservationForRecovery({
      reuseReservationRunId: reuseReservation?.runId,
      recoveryState: reuseReservation?.state,
    });

    if (requiresReservation && !skipReservation) {
      const reserved = await reserveTokens(runId, projectId, chapterNum, normalizedOperation, amount, {
        creditTier: currentTier,
        maxChaptersAllowed: maxChapters,
        paidEntitlementId,
        exportUnlocked
      });
      if (!reserved.ok) {
        const rejectionError = reserved.error || { kind: 'unknown' as const, message: 'Generation blocked: Failed to reserve tokens.' };
        const rejectionOutcome = resolveReservationRejectionOutcome({
          error: rejectionError,
          projectId,
          chapterNumber: chapterNum,
          targetChapterCount: targetChapterCount || maxChapters || 20,
          manuscriptCreditBalances,
        });

        addLog(
          'System',
          rejectionOutcome.creatorFeedMessage,
          rejectionOutcome.openPaywall ? 'warning' : 'error',
        );
        if (rejectionOutcome.technicalFailure) {
          setPaywallModal({ isOpen: false, reason: 'generation' });
          if (rejectionError.reason === 'invalid_operation') {
            logManuscriptReservationContractFailure({
              operation,
              normalizedOperation,
              acceptedOperations: [...ACCEPTED_RESERVATION_OPERATIONS],
              projectId,
              chapterNumber: chapterNum,
              paywallOpened: false,
            });
          }
          console.warn('[RESERVATION REJECTED]', rejectionError.reason || rejectionError.kind, {
            projectId,
            chapterNum,
            runId,
          });
        }

        if (rejectionOutcome.openPaywall && rejectionOutcome.paywallState) {
          setPaywallModal(rejectionOutcome.paywallState);
          if (rejectionOutcome.diagnostic) {
            logManuscriptCreditRequiredDiagnostic(rejectionOutcome.diagnostic);
          }
          void refreshUserData?.();
        } else if (rejectionOutcome.notifyReservationBlocked) {
          onReservationBlocked?.(rejectionError);
        }

        setIsAutoRunning(false);
        setStatus(AppStatus.IDLE);
        clearGenerationUi?.();
        setActiveGenerationRun(null, null);
        return false;
      }
      reservationMade = true;
    } else if (skipReservation) {
      reservationMade = true;
      console.log(`[RESERVATION REUSED] ${JSON.stringify({ runId, projectId, state: reuseReservation?.state })}`);
    }

    try {
      await setDoc(projectRef, {
        activeRunId: runId,
        activeChapterNum: chapterNum,
        generationStartedAt: new Date().toISOString(),
        generationStatus: 'running',
        lastGenerationHeartbeatAt: new Date().toISOString(),
      }, { merge: true });
    } catch (e: any) {
      addLog('System', `Failed to mark generation as running after reservation: ${e.message}`, 'error');
      if (reservationMade) {
        await releaseTokenReservation(runId, 'project_lock_failed');
      }
      setIsAutoRunning(false);
      setActiveGenerationRun(null, null);
      return false;
    }

    let success = false;
    let reservationDisposition: 'consume' | 'release' | 'retain_awaiting_physical' = 'release';
    const heartbeatTimer = window.setInterval(() => {
      void writeGenerationHeartbeat(uid, projectId, runId, chapterNum);
    }, 60_000);

    try {
      const callbackResult = await generationCallback(runId);
      const normalized = normalizeGenerationCallbackResult(callbackResult);
      success = normalized.success;
      reservationDisposition = normalized.reservationDisposition;
    } catch (e: any) {
      addLog('System', `Generation callback threw exception: ${e.message}`, 'error');
      success = false;
      reservationDisposition = 'release';
    } finally {
      window.clearInterval(heartbeatTimer);
      if (reservationMade) {
        try {
          if (reservationDisposition === 'consume' && success) {
            await consumeTokenReservation(runId);
            console.log(`[RESERVATION CONSUMED] ${JSON.stringify({ runId, projectId })}`);
          } else if (reservationDisposition === 'release') {
            console.log(`[RESERVATION RELEASED] ${JSON.stringify({ runId, projectId, reason: 'generation_failed' })}`);
            await releaseTokenReservation(runId, 'generation_failed');
          } else if (reservationDisposition === 'retain_awaiting_physical') {
            console.log(`[RESERVATION RETAINED] ${JSON.stringify({ runId, projectId, reason: 'awaiting_physical_extraction' })}`);
          }
        } catch (tokenErr: any) {
          console.warn('[Lifecycle] Token reservation finalization failed:', tokenErr.message);
        }
      }

      await finishGenerationRun(
        uid, projectId, runId,
        success ? 'idle' : reservationDisposition === 'retain_awaiting_physical' ? 'needs_user_review' : 'failed',
        success ? undefined : reservationDisposition === 'retain_awaiting_physical'
          ? 'awaiting_physical_extraction'
          : 'generation_callback_failed',
      );
      if (!success) {
        console.log(`[GENERATION LOCK RELEASED] ${JSON.stringify({ runId, projectId, reason: reservationDisposition })}`);
      }
      setActiveGenerationRun(null, null);
    }

    if (!success) {
      setIsAutoRunning(false);
    }
    return success;
  }, []);

  return { runGenerationLifecycle, finishGenerationRun };
}
